import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { Search, ChevronDown, Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Searchable Combobox - dropdown with search & optional custom value
 * Props:
 *   value: string
 *   onValueChange: (val: string) => void
 *   options: Array<{ value: string, label: string }> | string[]
 *   placeholder?: string
 *   searchPlaceholder?: string
 *   className?: string
 *   allowCustom?: boolean  - allow typing value not in list
 *   disabled?: boolean
 *   size?: 'sm' | 'default'
 */
const SearchableCombobox = memo(function SearchableCombobox({
  value,
  onValueChange,
  options = [],
  placeholder = 'Pilih...',
  searchPlaceholder = 'Ketik untuk cari...',
  className,
  allowCustom = true,
  disabled = false,
  size = 'default',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Normalize options to { value, label } format (memoized for high speed)
  const normalized = useMemo(() => {
    return options.map(o =>
      typeof o === 'string' ? { value: o, label: o } : o
    );
  }, [options]);

  const selected = useMemo(() => {
    return normalized.find(o => o.value === value);
  }, [normalized, value]);

  const filtered = useMemo(() => {
    if (!query) return normalized;
    const q = query.toLowerCase();
    return normalized.filter(o => o.label.toLowerCase().includes(q));
  }, [normalized, query]);

  const showAddCustom = useMemo(() => {
    if (!allowCustom || !query) return false;
    const q = query.toLowerCase();
    return !normalized.some(o => o.value.toLowerCase() === q);
  }, [allowCustom, query, normalized]);

  const [dropUp, setDropUp] = useState(false);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // If space below is less than 260px and space above is greater, open upward
      if (spaceBelow < 260 && rect.top > 260) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }
    }
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = (val) => {
    onValueChange(val);
    setOpen(false);
    setQuery('');
  };

  const handleAddCustom = () => {
    onValueChange(query);
    setOpen(false);
    setQuery('');
  };

  const heightCls = size === 'sm' ? 'h-8 text-xs' : 'h-9 text-sm';

  return (
    <div ref={ref} className={cn('relative', open ? 'z-[100]' : 'z-auto', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={cn(
          'flex w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring transition-colors',
          heightCls,
          disabled && 'opacity-50 cursor-not-allowed',
          open && 'ring-1 ring-ring border-ring'
        )}
      >
        <span className={cn('truncate', !selected && !value && 'text-muted-foreground')}>
          {selected ? selected.label : (value || placeholder)}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {value && (
            <X 
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" 
              onClick={(e) => {
                e.stopPropagation();
                onValueChange("");
              }}
            />
          )}
          <ChevronDown className={cn('h-4 w-4 opacity-50 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div 
          className={cn(
            "absolute z-[9999] w-full rounded-xl border border-border bg-popover shadow-2xl overflow-hidden",
            dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"
          )}
          style={{ left: 0, minWidth: '180px' }}
        >
          {/* Search bar */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 bg-muted/30">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  if (showAddCustom) handleAddCustom();
                  else if (filtered.length > 0) handleSelect(filtered[0].value);
                }
                if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              }}
            />
          </div>

          <div className="max-h-60 overflow-y-auto py-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {filtered.length === 0 && !showAddCustom ? (
              <div className="text-center py-6 px-4 flex flex-col items-center gap-2">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {allowCustom 
                    ? 'Ketik nama baru di kolom pencarian atas untuk menambahkannya ke daftar.' 
                    : 'Pilihan tidak ditemukan'}
                </p>
              </div>
            ) : (
              <>
                {filtered.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(opt.value);
                    }}
                    onClick={() => handleSelect(opt.value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                      value === opt.value && 'bg-accent/50 font-medium'
                    )}
                  >
                    <Check className={cn('w-3.5 h-3.5 shrink-0 text-primary', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{opt.label}</span>
                  </button>
                ))}

                {showAddCustom && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAddCustom();
                    }}
                    onClick={handleAddCustom}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 transition-colors border-t border-border mt-1"
                  >
                    <Plus className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Tambah "<strong>{query}</strong>"</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default SearchableCombobox;
